/**
 * Notifications bell — lives in the TopHeader right cluster, before the avatar.
 *
 * - Ghost icon button matching the header chrome, with an unread badge in the
 *   brand-purple accent (NOT destructive-red — that reads as an error).
 * - Click opens a popover preview: recent items, "Mark all read", "View all".
 * - Acting from the popover updates the badge instantly (optimistic cache).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { NotificationRow } from './NotificationRow';
import {
  useNotificationActions,
  useNotificationPreview,
  useUnreadCount,
} from './useNotifications';
import type { NotificationItem } from './notifications-procedures';

export function NotificationsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const unreadQuery = useUnreadCount();
  const previewQuery = useNotificationPreview(open);
  const { markRead, markAllRead } = useNotificationActions();

  const unread = unreadQuery.data ?? 0;
  const badgeLabel = unread > 9 ? '9+' : String(unread);
  const items = previewQuery.data?.items ?? [];
  const firstLoad = open && previewQuery.isLoading;

  const handleSelect = (item: NotificationItem) => {
    if (!item.read_at) markRead.mutate({ ids: [item.id] });
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={
            unread > 0
              ? t('notifications.bell.ariaCount', {
                  count: unread,
                  defaultValue: 'Notifications, {{count}} unread',
                })
              : t('notifications.title', { defaultValue: 'Notifications' })
          }
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-threadheads-purple px-1 text-[10px] font-semibold leading-none text-white tabular-nums"
            >
              {badgeLabel}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2.5">
          <p className="text-sm font-semibold">
            {t('notifications.title', { defaultValue: 'Notifications' })}
          </p>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-threadheads-purple transition-colors hover:bg-threadheads-purple/10 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t('notifications.markAllRead', { defaultValue: 'Mark all read' })}
            </button>
          )}
        </div>

        {/* Body */}
        {firstLoad ? (
          <div className="space-y-1 p-2" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2.5 px-2 py-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-4/5" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Check className="h-5 w-5" />
            </span>
            <p className="text-sm font-medium">
              {t('notifications.empty.title', { defaultValue: "You're all caught up" })}
            </p>
            <p className="max-w-[240px] text-xs text-muted-foreground">
              {t('notifications.empty.subtitle', {
                defaultValue: 'New mentions, assignments, and alerts will show up here.',
              })}
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            <div className="p-1.5">
              {items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  variant="popover"
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t p-1.5">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className={cn(
              'w-full rounded-md py-2 text-center text-xs font-medium text-muted-foreground',
              'transition-colors hover:bg-muted hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            {t('notifications.viewAll', { defaultValue: 'View all' })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
